import { trackEvent } from '~/features/analytics/track-event';
import { FlashSaleBanner } from '~/features/promotions/components/flash-sale-banner';
import { FlashSalePromoCard } from '~/features/promotions/components/flash-sale-promo-card';
import {
  type OctFlash2026Variant,
  getOctFlash2026CheckoutHref,
  OCT_FLASH_2026_CTA_COLOR,
  OCT_FLASH_2026_CTA_LABEL,
  OCT_FLASH_2026_EYEBROW_LABEL,
  OCT_FLASH_2026_VARIANTS,
} from '~/features/promotions/constants/oct-flash-2026';

import type { PromoCampaignSurfaceRenderers } from '../types';

const OCT_FLASH_2026_ANALYTICS_LABEL = 'oct_flash_2026_sale_clicked';

export const octFlash2026CampaignSurfaces: PromoCampaignSurfaceRenderers<OctFlash2026Variant>['surfaces'] = {
  homepage: ({ resolved, promoCode, imagePriority }) => {
    const config = OCT_FLASH_2026_VARIANTS[resolved];
    return (
      <FlashSalePromoCard
        backgroundGradient={config.backgroundGradient}
        backgroundImageBlendMode={config.patternBlendMode}
        backgroundImageDesktop={config.homepagePatternDesktop}
        backgroundImageMobile={config.homepagePatternMobile}
        backgroundImageMobileObjectPosition={config.homepagePatternMobileObjectPosition}
        backgroundImageOpacity={config.homepagePatternOpacity}
        checkoutHref={getOctFlash2026CheckoutHref(resolved, promoCode)}
        ctaColor={OCT_FLASH_2026_CTA_COLOR}
        ctaLabel={OCT_FLASH_2026_CTA_LABEL}
        eyebrowColor={config.eyebrowColor}
        eyebrowIcon='clock'
        eyebrowLabel={OCT_FLASH_2026_EYEBROW_LABEL}
        headline={config.headline}
        id={`oct-flash-2026-${resolved}-title`}
        imagePriority={imagePriority}
        stampImageDesktop={config.homepageStampDesktop}
        stampImageMobile={config.homepageStampMobile}
        stampOffsetRightDesktop={config.homepageStampOffsetRightDesktop}
        stampSizeDesktop={config.homepageStampSizeDesktop}
        stampSizeMobile={config.homepageStampSizeMobile}
        subtitle={config.homepageSubtitle}
        subtitleColor={config.subtitleColor}
        onClick={() =>
          trackEvent({
            type: 'click',
            name: 'Homepage CTA Clicked',
            label: OCT_FLASH_2026_ANALYTICS_LABEL,
          })
        }
      />
    );
  },
  catalog: ({ resolved, promoCode, imagePriority }) => {
    const config = OCT_FLASH_2026_VARIANTS[resolved];
    return (
      <FlashSaleBanner
        backgroundGradient={config.backgroundGradient}
        backgroundImageBlendMode={config.patternBlendMode}
        backgroundImageDesktop={config.catalogPatternDesktop}
        backgroundImageMobile={config.catalogPatternMobile}
        backgroundImageOpacity={config.catalogPatternOpacity}
        borderColor={config.borderColor}
        checkoutHref={getOctFlash2026CheckoutHref(resolved, promoCode)}
        ctaColor={OCT_FLASH_2026_CTA_COLOR}
        ctaLabel={OCT_FLASH_2026_CTA_LABEL}
        eyebrowColor={config.eyebrowColor}
        headline={config.catalogHeadline}
        imagePriority={imagePriority}
        subtitle={config.catalogSubtitle}
        subtitleColor={config.subtitleColor}
        hideEyebrow
        onCtaClick={() =>
          trackEvent({
            type: 'click',
            name: 'Catalog Page CTA Clicked',
            label: OCT_FLASH_2026_ANALYTICS_LABEL,
          })
        }
      />
    );
  },
  experience: ({ resolved, promoCode }) => {
    const config = OCT_FLASH_2026_VARIANTS[resolved];

    return {
      id: `oct_flash_2026_${resolved}`,
      promoVariant: 'flash_sale',
      eyebrow: OCT_FLASH_2026_EYEBROW_LABEL,
      title: config.experienceHeadline,
      description: config.experienceSubtitle,
      ctaLabel: OCT_FLASH_2026_CTA_LABEL,
      flashSale: {
        backgroundGradient: config.experienceBackgroundGradient,
        backgroundImage: config.experiencePattern,
        backgroundImageBlendMode: config.patternBlendMode,
        backgroundImageObjectPosition: 'center',
        bodyColor: config.subtitleColor,
        eyebrowColor: config.eyebrowColor,
        stampImageDesktop: config.experienceStampDesktop,
        stampImageMobile: config.experienceStampMobile,
      },
      action: {
        type: 'link',
        href: getOctFlash2026CheckoutHref(resolved, promoCode),
      },
    };
  },
};
